/**
 * @fileoverview Dynamic.xyz SDK configuration for wallet and social authentication
 * @module lib/dynamic
 */

import type { EvmNetwork } from '@dynamic-labs/sdk-react-core';

/**
 * Network environment from Vite config.
 */
const networkEnv = import.meta.env.VITE_NETWORK || 'testnet';

/**
 * EVM network configurations for Dynamic SDK.
 *
 * Testnet networks for development and staging.
 */
const testnetNetworks: EvmNetwork[] = [
  {
    chainId: 43113,
    networkId: 43113,
    name: 'Avalanche Fuji',
    iconUrls: ['https://cryptologos.cc/logos/avalanche-avax-logo.png'],
    nativeCurrency: {
      name: 'AVAX',
      symbol: 'AVAX',
      decimals: 18,
    },
    rpcUrls: ['https://api.avax-test.network/ext/bc/C/rpc'],
    blockExplorerUrls: ['https://testnet.snowtrace.io/'],
    vanityName: 'Avalanche Fuji',
  },
];

/**
 * Mainnet networks for production.
 */
const mainnetNetworks: EvmNetwork[] = [
  {
    chainId: 43114,
    networkId: 43114,
    name: 'Avalanche',
    iconUrls: ['https://cryptologos.cc/logos/avalanche-avax-logo.png'],
    nativeCurrency: {
      name: 'AVAX',
      symbol: 'AVAX',
      decimals: 18,
    },
    rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'],
    blockExplorerUrls: ['https://snowtrace.io/'],
    vanityName: 'Avalanche',
  },
  {
    chainId: 1,
    networkId: 1,
    name: 'Ethereum',
    iconUrls: ['https://cryptologos.cc/logos/ethereum-eth-logo.png'],
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://cloudflare-eth.com'],
    blockExplorerUrls: ['https://etherscan.io/'],
    vanityName: 'Ethereum',
  },
  {
    chainId: 42161,
    networkId: 42161,
    name: 'Arbitrum One',
    iconUrls: ['https://cryptologos.cc/logos/arbitrum-arb-logo.png'],
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://arb1.arbitrum.io/rpc'],
    blockExplorerUrls: ['https://arbiscan.io/'],
    vanityName: 'Arbitrum',
  },
];

/**
 * Get EVM networks based on current environment.
 *
 * @returns Array of EVM network configurations
 */
export function getEvmNetworks(): EvmNetwork[] {
  return networkEnv === 'testnet' ? testnetNetworks : mainnetNetworks;
}

/**
 * Dynamic SDK environment ID from env variables.
 */
export const dynamicEnvironmentId = import.meta.env.VITE_DYNAMIC_ENVIRONMENT_ID || '';

/**
 * Check if Dynamic is properly configured.
 *
 * @returns Whether Dynamic environment ID is set
 */
export function isDynamicConfigured(): boolean {
  return !!dynamicEnvironmentId;
}

/**
 * CSS variables for Dynamic widget theming to match Onicorn design.
 * Includes mobile-responsive styles for better touch interaction.
 * GPU acceleration hints added for smoother modal animations on mobile.
 */
export const dynamicCssOverrides = `
  .dynamic-widget-inline-controls {
    background-color: transparent;
  }

  /* Hide "My other wallets" section and "Link new" button - MOBILE ONLY */
  @media (max-width: 768px) {
    .dynamic-widget-wallets-header,
    .dynamic-widget-wallets,
    [data-testid="dynamicWidgetWallets"] {
      display: none !important;
    }
  }

  .dynamic-widget-card {
    background-color: #1a1a1a;
    border: 1px solid #2a2a2a;
  }

  .dynamic-widget-primary-button {
    background: linear-gradient(135deg, #44FF02 0%, #00FF00 100%);
    color: #000000;
    font-weight: 600;
    min-height: 44px;
  }

  .dynamic-widget-primary-button:hover {
    background: linear-gradient(135deg, #3DE000 0%, #00E600 100%);
  }

  /* Center the wallet details modal on screen */
  .dynamic-widget-modal,
  [class*="dynamic-widget-modal"],
  .dynamic-modal-card {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
  }

  /* GPU acceleration for smoother modal animations on mobile */
  .dynamic-modal,
  [data-dynamic-modal],
  [class*="dynamic-modal"] {
    will-change: transform, opacity;
    backface-visibility: hidden;
  }

  /* Ensure modal backdrop uses GPU compositing */
  .dynamic-modal-overlay,
  [class*="modal-overlay"],
  [class*="backdrop"] {
    will-change: opacity;
    transform: translateZ(0);
  }

  /* Mobile-specific adjustments for better touch interaction */
  @media (max-width: 768px) {
    /* Hide network picker on mobile - available inside modal */
    .network-picker-container,
    [class*="network-picker"],
    .dynamic-widget-inline-controls > button:first-child,
    [data-testid="network-picker"] {
      display: none !important;
    }

    .dynamic-widget-card {
      max-width: calc(100vw - 32px);
      margin: 16px;
      will-change: transform, opacity;
      transform: translateZ(0);
    }

    .dynamic-widget-primary-button {
      min-height: 48px;
      font-size: 16px;
    }

    .dynamic-widget-inline-controls button {
      min-height: 44px;
      min-width: 44px;
    }

    /* Position modal higher on mobile to avoid Android navigation bar */
    .dynamic-widget-modal,
    [class*="dynamic-widget-modal"],
    .dynamic-modal-card {
      top: 45% !important;
      max-height: calc(100dvh - env(safe-area-inset-bottom, 0px) - 32px);
      max-width: calc(100vw - 32px);
      width: calc(100vw - 32px) !important;
      /* Add bottom padding for safe area on notched devices */
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
  }
`;
